import { useMemo, useState, useSyncExternalStore } from "react";

import { AsyncBlock } from "../admin/bits";
import { MobileDrawer } from "../app/CollapsiblePanel";
import { DegradedBanner } from "../app/DegradedBanner";
import { formatAgo, formatPercent } from "../app/format";
import { usePersistentString } from "../app/persistentUi";
import { normalizeSearch } from "../app/search";
import { StreamBadge, Topbar, TopbarClock } from "../app/Topbar";
import { useStreamContext } from "../app/StreamProvider";
import { useBackend } from "../app/BackendProvider";
import { useIsMobile } from "../app/useIsMobile";
import { useResource } from "../app/useResource";
import { useConfig, useNetworkId } from "../config/ConfigProvider";
import { usePost } from "../posts/PostProvider";
import type { VehicleListItem, VehicleState } from "../contract/types";

/**
 * Onglet « Véhicules » (backlog §11, P2-46/47/48, contrat v1.18). Suivi de parc,
 * hors régulation : liste par exploitant avec « hors ligne depuis », agenda
 * d'affectation d'un véhicule, synoptique (Gantt) d'une journée. Aucune donnée
 * nominative — un véhicule = numéro de parc + clé de flux.
 */
const VIEWS = [
  { id: "liste", label: "Liste" },
  { id: "synoptique", label: "Synoptique" },
] as const;
type ViewId = (typeof VIEWS)[number]["id"];

function currentView(): ViewId {
  const seg = window.location.hash.replace(/^#\/vehicules\/?/, "").split("/")[0];
  return (VIEWS.find((v) => v.id === seg)?.id ?? "liste") as ViewId;
}
function useVehiclesView(): ViewId {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener("hashchange", cb);
      return () => window.removeEventListener("hashchange", cb);
    },
    currentView,
    () => "liste",
  );
}

const STATE_LABEL: Record<VehicleState, { label: string; tone: string }> = {
  online: { label: "en ligne", tone: "var(--sentinelle-ok)" },
  offline: { label: "hors ligne", tone: "#b45309" },
  gone: { label: "disparu", tone: "var(--sentinelle-signal)" },
};
/** Ordre de tri de l'état — pas alphabétique : du plus sain au plus problématique. */
const STATE_RANK: Record<VehicleState, number> = { online: 0, offline: 1, gone: 2 };

type SortKey = "displayNumber" | "flux" | "state" | "lastSeenAt" | "offlineSince";
type SortDir = "asc" | "desc";

function compareVehicles(a: VehicleListItem, b: VehicleListItem, key: SortKey): number {
  switch (key) {
    case "displayNumber":
      return a.displayNumber.localeCompare(b.displayNumber, "fr", { numeric: true });
    case "flux":
      return (a.exploitantId ?? a.operatorId).localeCompare(b.exploitantId ?? b.operatorId, "fr");
    case "state":
      return STATE_RANK[a.state] - STATE_RANK[b.state];
    case "lastSeenAt":
      return new Date(a.lastSeenAt).getTime() - new Date(b.lastSeenAt).getTime();
    case "offlineSince":
      // Jamais hors ligne (`null`) en fin de tri, quel que soit le sens.
      if (!a.offlineSince && !b.offlineSince) return 0;
      if (!a.offlineSince) return 1;
      if (!b.offlineSince) return -1;
      return new Date(a.offlineSince).getTime() - new Date(b.offlineSince).getTime();
  }
}

/** En-tête de colonne triable — `aria-sort` reflète l'état pour les lecteurs d'écran. */
function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const isActive = sortKey === active;
  return (
    <th aria-sort={isActive ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontWeight: isActive ? 700 : undefined,
        }}
      >
        {label}
        <span aria-hidden style={{ fontSize: "0.7em", opacity: isActive ? 1 : 0.3 }}>
          {isActive && dir === "desc" ? "▾" : "▴"}
        </span>
      </button>
    </th>
  );
}

export function VehiclesScreen() {
  const { status } = useStreamContext();
  const view = useVehiclesView();

  return (
    <div className="mae-app">
      <Topbar
        current="vehicles"
        right={
          <>
            <StreamBadge status={status} />
            <TopbarClock />
          </>
        }
      />
      <DegradedBanner />

      <nav
        className="tabs is-boxed"
        style={{ margin: 0, background: "var(--sentinelle-surface)", borderBottom: "1px solid var(--sentinelle-rule)" }}
      >
        <ul>
          {VIEWS.map((v) => (
            <li key={v.id} className={v.id === view ? "is-active" : ""}>
              <a className="ovl" href={`#/vehicules/${v.id}`}>
                {v.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mae-fill mae-scroll-y" style={{ padding: "18px 22px", background: "var(--sentinelle-surface)" }}>
        {view === "synoptique" ? <GanttView /> : <ListView />}
      </div>
    </div>
  );
}

function ExploitantSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const config = useConfig();
  return (
    <span className="select is-small">
      <label className="is-sr-only">Exploitant</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tous les exploitants</option>
        {config.exploitants.map((exp) => (
          <option key={exp.id} value={exp.id}>
            {exp.label}
          </option>
        ))}
      </select>
    </span>
  );
}

function ListView() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const config = useConfig();
  const { activePost } = usePost();
  const isMobile = useIsMobile();
  const [exploitant, setExploitant] = usePersistentString("sentinelle.vehicles.exploitant");
  const [selected, setSelected] = useState<VehicleListItem | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("displayNumber");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function onSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const query = useResource(
    (signal) =>
      backend.api.listVehicles(
        networkId,
        { ...(exploitant ? { exploitant } : {}), ...(activePost ? { post: activePost.id } : {}) },
        signal,
      ),
    [backend, networkId, exploitant, activePost],
  );
  const now = Date.now();
  const allVehicles = query.data?.vehicles ?? [];
  const hiddenCount = query.data?.hiddenOperatorIds.length ?? 0;

  // Recherche par n° de véhicule (demande utilisateur) — insensible casse/accents,
  // sur le seul numéro affiché (identifiant lu au poste, pas de recherche sur des
  // champs techniques que le régulateur ne tape jamais).
  const term = normalizeSearch(search);
  const filtered = term ? allVehicles.filter((v) => normalizeSearch(v.displayNumber).includes(term)) : allVehicles;
  const vehicles = useMemo(
    () => [...filtered].sort((a, b) => (sortDir === "asc" ? 1 : -1) * compareVehicles(a, b, sortKey)),
    [filtered, sortKey, sortDir],
  );

  // D-020 (v1.46) — colonne dérivée côté front, pas de champ serveur dédié :
  // visible si aucun poste actif, ou si le poste régule un flux masqué, ou
  // s'il couvre plusieurs flux (l'info redevient utile pour distinguer).
  const showFluxColumn =
    !activePost ||
    activePost.operatorIds.some((id) => config.fleetView.hiddenOperatorIds.includes(id)) ||
    activePost.operatorIds.length > 1;

  return (
    <div
      style={{
        display: "grid",
        // Mobile (demande utilisateur 2026-09-11) : jamais de 2e colonne fixe
        // (320-420px, la moitié d'un écran de téléphone) — l'agenda s'ouvre en
        // tiroir plein écran à la place (plus bas).
        gridTemplateColumns: selected && !isMobile ? "minmax(0, 1fr) minmax(320px, 420px)" : "1fr",
        gap: 20,
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
            Parc — {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""}
          </h2>
          <ExploitantSelect value={exploitant ?? ""} onChange={(v) => setExploitant(v || null)} />
          <input
            type="search"
            className="input is-small"
            style={{ width: 160 }}
            placeholder="N° de véhicule…"
            aria-label="Rechercher un véhicule par numéro"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button is-small" onClick={query.reload}>
            Rafraîchir
          </button>
          {hiddenCount > 0 ? (
            <span className="has-text-grey is-size-7" title="Flux techniques hors périmètre supervision courante">
              {hiddenCount} flux masqué{hiddenCount > 1 ? "s" : ""} — choisir l'exploitant ci-dessus pour l'afficher
            </span>
          ) : null}
        </div>

        <AsyncBlock
          loading={query.loading && !query.data}
          error={query.error}
          onRetry={query.reload}
          empty={vehicles.length === 0}
          emptyLabel={term ? `Aucun véhicule ne correspond à « ${search} ».` : "Aucun véhicule pour cet exploitant."}
        >
          {/* Défilement horizontal plutôt que des colonnes illisibles sur téléphone
              (demande utilisateur 2026-09-11) — le tableau garde sa forme, la page ne
              déborde jamais. */}
          <div style={{ overflowX: "auto" }}>
            <table className="table is-fullwidth is-narrow is-hoverable">
              <thead>
                <tr>
                  <SortableTh label="N° parc" sortKey="displayNumber" active={sortKey} dir={sortDir} onSort={onSort} />
                  {showFluxColumn ? (
                    <SortableTh label="Exploitant / flux" sortKey="flux" active={sortKey} dir={sortDir} onSort={onSort} />
                  ) : null}
                  <SortableTh label="État" sortKey="state" active={sortKey} dir={sortDir} onSort={onSort} />
                  <SortableTh label="Dernier contact" sortKey="lastSeenAt" active={sortKey} dir={sortDir} onSort={onSort} />
                  <SortableTh label="Hors ligne depuis" sortKey="offlineSince" active={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => {
                  const st = STATE_LABEL[v.state];
                  return (
                    <tr
                      key={v.vehicleId}
                      onClick={() => setSelected(v)}
                      style={{
                        cursor: "pointer",
                        background: selected?.vehicleId === v.vehicleId ? "var(--bulma-scheme-main-bis)" : undefined,
                      }}
                    >
                      <td className="mono" style={{ fontWeight: 700 }}>
                        {v.displayNumber}
                      </td>
                      {showFluxColumn ? (
                        <td className="is-size-7">
                          {v.exploitantId ?? "—"} <span className="has-text-grey">· {v.operatorId}</span>
                        </td>
                      ) : null}
                      <td>
                        <span className="ovl" style={{ color: st.tone }}>
                          {st.label}
                        </span>
                      </td>
                      <td className="is-size-7 has-text-grey">{new Date(v.lastSeenAt).toLocaleString("fr-FR")}</td>
                      <td className="mono is-size-7">{v.offlineSince ? formatAgo(v.offlineSince, now) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBlock>
      </div>

      {selected && !isMobile ? <VehicleAgenda vehicle={selected} onClose={() => setSelected(null)} /> : null}
      {selected && isMobile ? (
        <MobileDrawer side="right" onClose={() => setSelected(null)}>
          <VehicleAgenda vehicle={selected} onClose={() => setSelected(null)} />
        </MobileDrawer>
      ) : null}
    </div>
  );
}

function VehicleAgenda({ vehicle, onClose }: { vehicle: VehicleListItem; onClose: () => void }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const query = useResource(
    (signal) => backend.api.getVehicleAgenda(networkId, vehicle.vehicleId, {}, signal),
    [backend, networkId, vehicle.vehicleId],
  );
  const a = query.data;

  return (
    <aside style={{ border: "1px solid var(--sentinelle-rule)", padding: "14px 16px", background: "var(--bulma-scheme-main-bis)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ font: "700 13px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
          Véhicule {vehicle.displayNumber} — agenda
        </h3>
        <button type="button" className="button is-small is-white" aria-label="Fermer" onClick={onClose} style={{ height: 30, width: 30, padding: 0 }}>
          ✕
        </button>
      </div>

      <AsyncBlock loading={query.loading && !query.data} error={query.error} onRetry={query.reload} empty={!a}>
        {a ? (
          <>
            <p className="has-text-grey is-size-7" style={{ marginBottom: 8 }}>
              {a.from} → {a.to} · activité {formatPercent(a.overallActivityRatio, 0)} ·{" "}
              {Math.round(a.totalActiveSeconds / 3600)} h en course
            </p>

            <div className="ovl" style={{ marginBottom: 4 }}>
              Répartition par ligne
            </div>
            <div style={{ display: "grid", gap: 4, marginBottom: 14 }}>
              {a.byLine.map((l) => (
                <div key={l.lineId} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.72rem" }}>
                  <span className="mono" style={{ width: 34, fontWeight: 700 }}>
                    {l.lineCode}
                  </span>
                  <span style={{ flex: 1, height: 8, background: "var(--sentinelle-rule)" }}>
                    <span style={{ display: "block", height: "100%", width: `${l.ratio * 100}%`, background: "var(--sentinelle-rail)" }} />
                  </span>
                  <span className="mono has-text-grey" style={{ width: 42, textAlign: "right" }}>
                    {formatPercent(l.ratio, 0)}
                  </span>
                </div>
              ))}
            </div>

            <div className="ovl" style={{ marginBottom: 4 }}>
              Par jour
            </div>
            <table className="table is-fullwidth is-narrow" style={{ fontSize: "0.7rem" }}>
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Lignes</th>
                  <th style={{ textAlign: "right" }}>Activité</th>
                </tr>
              </thead>
              <tbody>
                {a.days.map((d) => (
                  <tr key={d.date}>
                    <td className="mono">{d.date}</td>
                    <td className="mono">{d.lines.map((l) => l.lineCode).join(", ")}</td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {formatPercent(d.activityRatio, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </AsyncBlock>
    </aside>
  );
}

function GanttView() {
  const backend = useBackend();
  const networkId = useNetworkId();
  const [exploitant, setExploitant] = usePersistentString("sentinelle.vehicles.exploitant");
  const [date, setDate] = useState<string>(() => new Date(Date.now() - 86_400_000).toISOString().slice(0, 10));

  const query = useResource(
    (signal) => backend.api.getVehiclesGantt(networkId, { date, ...(exploitant ? { exploitant } : {}) }, signal),
    [backend, networkId, date, exploitant],
  );
  const data = query.data;

  const lineColor = useLineColor();
  // J.9 — dézoomé, calqué sur le Gantt des Services : graduation toutes les 2 h,
  // lignes plus hautes, barres plus épaisses (elles étaient trop serrées).
  const HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24];
  const W = 1180;
  const H_ROW = 30;

  const rows = data?.rows ?? [];
  const height = rows.length * H_ROW + 24;

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <h2 style={{ font: "700 15px/1.2 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
          Synoptique des véhicules
        </h2>
        <input type="date" className="input is-small" style={{ width: 150 }} value={date} onChange={(e) => setDate(e.target.value)} />
        <ExploitantSelect value={exploitant ?? ""} onChange={(v) => setExploitant(v || null)} />
      </div>

      <AsyncBlock
        loading={query.loading && !query.data}
        error={query.error}
        onRetry={query.reload}
        empty={rows.length === 0}
        emptyLabel="Aucun véhicule actif ce jour-là."
      >
        <div style={{ overflowX: "auto", border: "1px solid var(--sentinelle-rule)" }}>
          <svg width={W + 60} height={height} role="img" aria-label="Diagramme de Gantt des véhicules" style={{ display: "block" }}>
            {HOURS.map((h) => {
              const x = 56 + (h / 24) * W;
              return (
                <g key={h}>
                  <line x1={x} y1={0} x2={x} y2={height} stroke="var(--sentinelle-rule)" />
                  <text x={x + 2} y={12} fontSize={9} fill="var(--sentinelle-ink-3)">
                    {String(h).padStart(2, "0")} h
                  </text>
                </g>
              );
            })}
            {rows.map((row, i) => {
              const y = 20 + i * H_ROW;
              return (
                <g key={row.vehicleId}>
                  <text x={0} y={y + H_ROW / 2 + 3} fontSize={10} fontFamily="var(--bulma-family-code)" fill="var(--sentinelle-ink-2)">
                    {row.displayNumber}
                  </text>
                  {row.segments.map((s, k) => {
                    const x = 56 + (s.fromSeconds / 86_400) * W;
                    const w = Math.max(2, ((s.toSeconds - s.fromSeconds) / 86_400) * W);
                    return (
                      <rect key={k} x={x} y={y + 3} width={w} height={H_ROW - 8} rx={2} fill={lineColor(s.lineId)}>
                        <title>
                          {s.lineCode} · {fmtHm(s.fromSeconds)}–{fmtHm(s.toSeconds)}
                        </title>
                      </rect>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>
      </AsyncBlock>
    </>
  );
}

function fmtHm(seconds: number): string {
  const m = Math.round(seconds / 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function useLineColor() {
  const config = useConfig();
  return useMemo(() => {
    const palette = ["#2b6cb0", "#00963f", "#b45309", "#6d3b8a", "#c2410c", "#0e7490", "#a21caf"];
    const idx = new Map(config.lineGroups.flatMap((g) => g.lineIds).map((id, i) => [id, i]));
    return (lineId: string) => palette[(idx.get(lineId) ?? lineId.length) % palette.length]!;
  }, [config.lineGroups]);
}
